import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Upload, Sparkles, Loader2, FileText, ShieldCheck, Film, Users, Palette, Target,
  AlertTriangle, BookOpen, ArrowLeft, RefreshCw, ChevronRight, Download, Share2, Send, Check,
} from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api, apiUrl } from "@/lib/api";

const ACCEPT = ".pdf,.docx,.txt";

export default function ScriptAnalysis() {
  const { id: projectId } = useParams();
  const { t } = useTranslation();
  const [project, setProject] = useState(null);
  const [existing, setExisting] = useState(null);
  const [consentText, setConsentText] = useState("");
  const [consent, setConsent] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadedText, setUploadedText] = useState("");
  const [uploadedName, setUploadedName] = useState("");
  const [uploadToken, setUploadToken] = useState("");
  const [uploadTruncated, setUploadTruncated] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: ct }, { data: prev }] = await Promise.all([
          api.get(`/api/projects/${projectId}`),
          api.get("/api/script-analysis/consent-text"),
          api.get(`/api/script-analysis/project/${projectId}`),
        ]);
        setProject(p);
        setConsentText(ct.consent_text);
        setExisting(prev);
      } catch (e) {
        toast.error("No se pudo cargar el proyecto");
      }
    })();
  }, [projectId]);

  const doUpload = async () => {
    if (!file) return;
    if (!consent) {
      toast.error(t("script.consent_required"));
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("project_id", projectId);
      fd.append("consent", "true");
      fd.append("file", file);
      const { data } = await api.post("/api/script-analysis/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedText(data.extracted_text);
      setUploadedName(data.file_name);
      setUploadToken(data.upload_token);
      setUploadTruncated(!!data.truncated);
      if (data.truncated) {
        toast.warning(t("script.truncated_warning"));
      } else {
        toast.success(t("script.upload_ok", { chars: data.char_count }));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t("script.upload_failed"));
    } finally {
      setUploading(false);
    }
  };

  const doAnalyze = async () => {
    if (!uploadedText || !consent) return;
    setAnalyzing(true);
    try {
      const { data } = await api.post("/api/script-analysis/analyze", {
        project_id: projectId,
        consent: true,
        file_name: uploadedName,
        extracted_text: uploadedText,
        upload_token: uploadToken,
      });
      setExisting(data);
      setUploadedText("");
      setUploadedName("");
      setUploadToken("");
      setUploadTruncated(false);
      setFile(null);
      toast.success(t("script.analysis_ready"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("script.analyze_failed"));
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <DashboardLayout
      title={t("script.title")}
      subtitle={project ? project.title : ""}
      actions={
        <Link to={`/dashboard/proyectos/${projectId}`} className="text-sm text-slate-500 hover:text-petroleum inline-flex items-center gap-1" data-testid="link-back-project">
          <ArrowLeft size={14} /> {t("common.back")}
        </Link>
      }
    >
      {!existing ? (
        <UploadFlow
          t={t}
          file={file}
          setFile={setFile}
          consent={consent}
          setConsent={setConsent}
          consentText={consentText}
          uploading={uploading}
          analyzing={analyzing}
          uploadedText={uploadedText}
          uploadedName={uploadedName}
          doUpload={doUpload}
          doAnalyze={doAnalyze}
        />
      ) : (
        <AnalysisView
          t={t}
          data={existing}
          onNewUpload={() => setExisting(null)}
        />
      )}
    </DashboardLayout>
  );
}

function UploadFlow({ t, file, setFile, consent, setConsent, consentText, uploading, analyzing, uploadedText, uploadedName, doUpload, doAnalyze }) {
  return (
    <div className="max-w-3xl">
      <div className="kpi-card p-8">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={16} className="text-gold" />
          <p className="eyebrow text-gold">{t("script.eyebrow")}</p>
        </div>
        <h2 className="font-display text-2xl text-petroleum">{t("script.upload_title")}</h2>
        <p className="text-sm text-slate-500 mt-2">{t("script.upload_subtitle")}</p>

        <div className="mt-6">
          <label className="inline-flex items-center gap-3 rounded-xl border border-dashed border-slate-300 hover:border-gold px-6 py-6 cursor-pointer w-full transition" data-testid="label-file-input">
            <Upload size={18} className="text-gold" />
            <span className="text-sm">
              {file ? (
                <span className="text-petroleum font-medium">{file.name}</span>
              ) : (
                <span className="text-slate-500">{t("script.pick_file")}</span>
              )}
            </span>
            <input
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="input-script-file"
            />
          </label>
          <p className="text-[10px] text-slate-400 mt-2">{t("script.formats_hint")}</p>
        </div>

        <div className="mt-6 flex items-start gap-3 rounded-xl bg-slate-50 border border-slate-200 p-4">
          <Checkbox
            id="consent"
            checked={consent}
            onCheckedChange={(v) => setConsent(!!v)}
            data-testid="checkbox-consent"
            className="mt-0.5"
          />
          <label htmlFor="consent" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
            <ShieldCheck size={12} className="inline text-emerald-600 mr-1" />
            {consentText || t("script.consent_fallback")}
          </label>
        </div>

        {!uploadedText ? (
          <Button
            onClick={doUpload}
            disabled={!file || !consent || uploading}
            data-testid="button-upload-script"
            className="mt-6 gold-cta rounded-full px-6 py-6 font-semibold"
          >
            {uploading ? (
              <><Loader2 size={16} className="mr-2 animate-spin" /> {t("script.uploading")}</>
            ) : (
              <><Upload size={16} className="mr-2" /> {t("script.upload_cta")}</>
            )}
          </Button>
        ) : (
          <div className="mt-6 space-y-4">
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3">
              <FileText size={16} className="text-emerald-700 mt-0.5" />
              <div className="text-sm text-emerald-900">
                <p className="font-semibold">{uploadedName}</p>
                <p className="text-xs mt-0.5">{t("script.preview_hint", { chars: uploadedText.length })}</p>
              </div>
            </div>
            <Button
              onClick={doAnalyze}
              disabled={analyzing}
              data-testid="button-analyze-script"
              className="w-full gold-cta rounded-full py-6 font-semibold"
            >
              {analyzing ? (
                <><Loader2 size={16} className="mr-2 animate-spin" /> {t("script.analyzing")}</>
              ) : (
                <><Sparkles size={16} className="mr-2" /> {t("script.analyze_cta")}</>
              )}
            </Button>
            <p className="text-[10px] text-slate-400 text-center">{t("script.analyze_time_hint")}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function AnalysisView({ t, data, onNewUpload }) {
  const a = data.analysis || {};
  const [downloading, setDownloading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [sponsors, setSponsors] = useState({ priority: [], others: [] });
  const [selectedSponsor, setSelectedSponsor] = useState(null);
  const [shareMessage, setShareMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!shareOpen) return;
    (async () => {
      try {
        const { data: sp } = await api.get("/api/script-analysis/sponsors-available");
        setSponsors(sp);
      } catch (e) {
        console.error("sponsors_load_failed", e);
      }
    })();
  }, [shareOpen]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const resp = await api.get(`/api/script-analysis/${data.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `cinengage-analisis-${(data.file_name || "guion").replace(/[^a-z0-9]/gi, "_")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("script.pdf_ready"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("script.pdf_failed"));
    } finally {
      setDownloading(false);
    }
  };

  const shareWithSponsor = async () => {
    if (!selectedSponsor) return;
    setSending(true);
    try {
      await api.post(`/api/script-analysis/${data.id}/share`, {
        sponsor_id: selectedSponsor.id,
        message: shareMessage.trim() || null,
      });
      toast.success(t("script.share_ok", { name: selectedSponsor.name }));
      setShareOpen(false);
      setSelectedSponsor(null);
      setShareMessage("");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("script.share_failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="analysis-view">
      <div className="kpi-card p-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="eyebrow text-gold">{t("script.eyebrow")}</p>
          <h2 className="font-display text-2xl text-petroleum">{t("script.result_title")}</h2>
          <p className="text-xs text-slate-500 mt-1">
            {t("script.result_meta", {
              file: data.file_name,
              date: new Date(data.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric" }),
            })}
          </p>
          {data.truncated && (
            <p className="text-[10px] text-amber-600 mt-1">
              <AlertTriangle size={10} className="inline mr-1" /> {t("script.truncated_note")}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={() => setShareOpen(true)}
            data-testid="button-share-with-sponsor"
            variant="outline"
            className="rounded-full border-gold/40 text-petroleum hover:bg-gold/10"
          >
            <Share2 size={14} className="mr-2 text-gold" /> {t("script.share_cta")}
          </Button>
          <Button
            onClick={downloadPdf}
            disabled={downloading}
            data-testid="button-download-pdf"
            className="gold-cta rounded-full"
          >
            {downloading ? (
              <><Loader2 size={14} className="mr-2 animate-spin" /> {t("script.pdf_generating")}</>
            ) : (
              <><Download size={14} className="mr-2" /> {t("script.download_pdf")}</>
            )}
          </Button>
          <Button
            onClick={onNewUpload}
            variant="outline"
            data-testid="button-new-analysis"
            className="rounded-full"
          >
            <RefreshCw size={14} className="mr-2" /> {t("script.new_analysis")}
          </Button>
        </div>
      </div>

      <ShareDialog
        t={t}
        open={shareOpen}
        onOpenChange={setShareOpen}
        sponsors={sponsors}
        selectedSponsor={selectedSponsor}
        setSelectedSponsor={setSelectedSponsor}
        shareMessage={shareMessage}
        setShareMessage={setShareMessage}
        sending={sending}
        onSubmit={shareWithSponsor}
      />

      {a.synopsis && (
        <Section icon={BookOpen} title={t("script.section_synopsis")} testid="section-synopsis">
          <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{a.synopsis}</p>
        </Section>
      )}

      {Array.isArray(a.genres) && a.genres.length > 0 && (
        <Section icon={Film} title={t("script.section_genres")} testid="section-genres">
          <div className="flex flex-wrap gap-2">
            {a.genres.map((g) => (
              <div key={g.label} className="rounded-full border border-gold/40 bg-gold/10 text-petroleum px-3 py-1.5 text-xs flex items-center gap-2" data-testid={`genre-${g.label}`}>
                <span className="font-semibold">{g.label}</span>
                <span className="text-[10px] tabular-nums text-slate-500">{Math.round((g.confidence || 0) * 100)}%</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {Array.isArray(a.characters) && a.characters.length > 0 && (
        <Section icon={Users} title={t("script.section_characters")} testid="section-characters">
          <ul className="space-y-3">
            {a.characters.map((c) => (
              <li key={c.name} className="rounded-lg border border-slate-200 p-3">
                <p className="font-display text-petroleum">{c.name}</p>
                <p className="text-xs text-slate-600 mt-1">{c.arc}</p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {Array.isArray(a.themes) && a.themes.length > 0 && (
        <Section icon={Palette} title={t("script.section_themes")} testid="section-themes">
          <div className="flex flex-wrap gap-2">
            {a.themes.map((th) => (
              <Badge key={th} variant="secondary" className="rounded-full">{th}</Badge>
            ))}
          </div>
          {a.tone && (
            <p className="text-xs text-slate-500 mt-3">
              <b>{t("script.tone")}:</b> {a.tone}
            </p>
          )}
        </Section>
      )}

      {a.audience && (
        <Section icon={Target} title={t("script.section_audience")} testid="section-audience">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label={t("script.audience_age")} value={a.audience.recommended_age} />
            <Field label={t("script.audience_demo")} value={a.audience.demographic} />
          </div>
        </Section>
      )}

      {Array.isArray(a.brand_categories) && a.brand_categories.length > 0 && (
        <Section icon={Sparkles} title={t("script.section_brands")} testid="section-brands" highlight>
          <ul className="space-y-3">
            {a.brand_categories.map((b) => (
              <li key={b.category} className="rounded-lg border border-gold/30 bg-gold/5 p-3">
                <div className="flex items-center gap-2">
                  <ChevronRight size={14} className="text-gold" />
                  <p className="font-display text-petroleum">{b.category}</p>
                </div>
                {b.reason && <p className="text-xs text-slate-600 mt-1">{b.reason}</p>}
                {b.scenes_hint && <p className="text-[10px] text-slate-500 mt-1"><i>{b.scenes_hint}</i></p>}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {Array.isArray(a.content_risks) && a.content_risks.length > 0 && (
        <Section icon={AlertTriangle} title={t("script.section_risks")} testid="section-risks">
          <ul className="space-y-2">
            {a.content_risks.map((r) => (
              <li key={r.type} className="flex items-start gap-3 rounded-lg bg-slate-50 border border-slate-200 p-3">
                <RiskDot level={r.level} />
                <div className="text-xs text-slate-700">
                  <b className="uppercase tracking-widest text-[10px] text-slate-500">{r.type}</b>
                  <span className="ml-2 text-[10px] text-slate-500">({r.level})</span>
                  <p className="mt-0.5">{r.note}</p>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {Array.isArray(a.comparables) && a.comparables.length > 0 && (
        <Section icon={Film} title={t("script.section_comparables")} testid="section-comparables">
          <ul className="space-y-2">
            {a.comparables.map((cp) => (
              <li key={cp} className="text-sm text-slate-700 pl-4 border-l-2 border-gold/40">{cp}</li>
            ))}
          </ul>
        </Section>
      )}

      <div className="text-[10px] text-slate-400 pt-2">
        <ShieldCheck size={10} className="inline mr-1 text-emerald-600" />
        {t("script.consent_footer", { date: new Date(data.consent_given_at).toLocaleString() })}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children, testid, highlight }) {
  return (
    <div className={`kpi-card p-6 ${highlight ? "border-gold/40" : ""}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className="text-gold" />
        <h3 className="font-display text-lg text-petroleum">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className="text-sm text-petroleum mt-1">{value || "—"}</p>
    </div>
  );
}

function RiskDot({ level }) {
  const tone =
    level === "high" ? "bg-red-500" :
    level === "medium" ? "bg-amber-500" : "bg-emerald-500";
  return <span className={`h-2 w-2 rounded-full mt-1.5 ${tone}`} />;
}

function ShareDialog({ t, open, onOpenChange, sponsors, selectedSponsor, setSelectedSponsor, shareMessage, setShareMessage, sending, onSubmit }) {
  const allSponsors = [...(sponsors.priority || []), ...(sponsors.others || [])];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dialog-share-analysis">
        <DialogHeader>
          <DialogTitle className="font-display text-petroleum">{t("script.share_title")}</DialogTitle>
          <DialogDescription>{t("script.share_description")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">{t("script.share_pick_sponsor")}</p>
            {allSponsors.length === 0 ? (
              <p className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-4 text-center">
                {t("script.share_no_sponsors")}
              </p>
            ) : (
              <div className="max-h-56 overflow-y-auto space-y-1.5" data-testid="share-sponsor-list">
                {(sponsors.priority || []).length > 0 && (
                  <p className="text-[10px] uppercase tracking-widest text-emerald-600 font-semibold">{t("script.share_priority")}</p>
                )}
                {(sponsors.priority || []).map((s) => (
                  <SponsorRow key={s.id} s={s} selected={selectedSponsor?.id === s.id} onSelect={setSelectedSponsor} />
                ))}
                {(sponsors.others || []).length > 0 && (
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mt-2">{t("script.share_others")}</p>
                )}
                {(sponsors.others || []).map((s) => (
                  <SponsorRow key={s.id} s={s} selected={selectedSponsor?.id === s.id} onSelect={setSelectedSponsor} />
                ))}
              </div>
            )}
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">{t("script.share_message_label")}</p>
            <Textarea
              value={shareMessage}
              onChange={(e) => setShareMessage(e.target.value)}
              placeholder={t("script.share_message_placeholder")}
              rows={3}
              data-testid="textarea-share-message"
              className="resize-none"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-share-cancel">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!selectedSponsor || sending}
            data-testid="button-share-send"
            className="gold-cta rounded-full"
          >
            {sending ? (
              <><Loader2 size={14} className="mr-2 animate-spin" /> {t("script.share_sending")}</>
            ) : (
              <><Send size={14} className="mr-2" /> {t("script.share_send")}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SponsorRow({ s, selected, onSelect }) {
  return (
    <button
      onClick={() => onSelect(s)}
      data-testid={`share-sponsor-${s.id}`}
      className={`w-full flex items-center gap-3 rounded-lg border px-3 py-2 text-left transition ${selected ? "border-gold bg-gold/10" : "border-slate-200 hover:border-gold/40"}`}
    >
      <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-petroleum font-display overflow-hidden shrink-0">
        {s.avatar_url ? <img src={s.avatar_url} alt="" className="h-full w-full object-cover" /> : (s.name || "?").slice(0, 1).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-petroleum truncate">{s.name}</p>
        <p className="text-[10px] text-slate-500 truncate">{s.email}</p>
      </div>
      {selected && <Check size={14} className="text-gold shrink-0" />}
    </button>
  );
}
